import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Accessible name for the dialog when no visible `title` is rendered. */
  ariaLabel?: string;
  children: ReactNode;
  size?: 'md' | 'lg' | 'full';
}

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

const sizeClasses: Record<NonNullable<ModalProps['size']>, string> = {
  md: 'w-full max-w-lg max-h-[85vh]',
  lg: 'w-full max-w-3xl max-h-[85vh]',
  full: 'h-[90vh] w-full max-w-[95vw]',
};

/**
 * Centered dialog rendered in a portal. Closes on backdrop click and ESC,
 * locks body scroll while open.
 */
export function Modal({ open, onClose, title, ariaLabel, children, size = 'md' }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // ESC closes.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Focus-trap-lite: move focus into the dialog when it opens.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const closeButton = (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className="rounded-full p-2 text-ink-secondary transition-all duration-200 hover:bg-line-light hover:text-ink"
    >
      <X size={20} aria-hidden="true" />
    </button>
  );

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title ?? ariaLabel}
        tabIndex={-1}
        className={cn(
          'relative flex flex-col overflow-hidden rounded-3xl bg-surface shadow-2xl animate-fade-up outline-none',
          sizeClasses[size],
        )}
      >
        {title ? (
          <div className="flex shrink-0 items-center justify-between gap-4 border-b border-line px-5 py-4">
            <h2 className="truncate text-lg font-extrabold text-ink">{title}</h2>
            {closeButton}
          </div>
        ) : (
          <div className="absolute right-3 top-3 z-10">{closeButton}</div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
